import { compact, isString, join, map, reject, size } from "lodash";
import { Platform } from "@linkfluence/models/platform";
function surrounded(middle: string) {
  return function surround(...props: string[]) {
    const queries = compact(props);
    if (size(queries)) {
      return join(queries, ` ${middle} `);
    } else {
      return "";
    }
  };
}

function surrounder(open: string, close: string) {
  return function (...props: string[]) {
    const queries = compact(props);
    if (size(queries)) {
      return `${open}${join(queries, "")}${close}`;
    } else {
      return "";
    }
  };
}

export const and = surrounded("AND");

export const or = surrounded("OR");

export const group = surrounder("(", ")");

export function field(field: string, query: string) {
  if (query === "") {
    return "";
  }
  return `${field}:${query}`;
}

export function getPlatformSearchQueryField(platform: Platform) {
  return field("origin.platform", platform);
}

type SearchQueryTerm =
  | string
  | SearchQueryField
  | SearchQueryAnd
  | SearchQueryOr;

export class SearchQueryField {
  private field: string;
  private query: string;
  public constructor(field: string, query: string) {
    this.field = field;
    this.query = query;
  }
  public getQuery() {
    return field(this.field, this.query);
  }
  static getInstance(field: string, query: string) {
    return new SearchQueryField(field, query);
  }
  static getPlatformField(platform: Platform) {
    return new SearchQueryField("origin.platform", platform);
  }
  static getReversedUrlField(reversedUrl: string) {
    return new SearchQueryField("url.site.reversed", `"${reversedUrl}"`);
  }
  static getSubRedditField(screenName: string) {
    return new SearchQueryField("reddit.subreddit.screen-name", screenName);
  }
  static getUserIdField(platform: Platform, userId: string | number) {
    return new SearchQueryField(`user.${platform}.id`, "" + userId);
  }
  static getAuthorScreenNameField(platform: Platform, screenName: string) {
    return new SearchQueryField(`user.${platform}.author`, screenName);
  }
}

function getGroupedQuery(term: SearchQueryTerm) {
  if (term instanceof SearchQueryAnd || term instanceof SearchQueryOr) {
    return group(term.getQuery());
  }
  return isString(term) ? term : term.getQuery();
}

export class SearchQueryAnd {
  private termA: SearchQueryTerm;
  private termB: SearchQueryTerm;
  public constructor(termA: SearchQueryTerm, termB: SearchQueryTerm) {
    this.termA = termA;
    this.termB = termB;
  }
  public getQuery() {
    return and(getGroupedQuery(this.termA), getGroupedQuery(this.termB));
  }
  static getInstance(termA: SearchQueryTerm, termB: SearchQueryTerm) {
    return new SearchQueryAnd(termA, termB);
  }
}

export class SearchQueryOr {
  private termA: SearchQueryTerm;
  private termB: SearchQueryTerm;
  public constructor(termA: SearchQueryTerm, termB: SearchQueryTerm) {
    this.termA = termA;
    this.termB = termB;
  }
  public getQuery() {
    return or(getGroupedQuery(this.termA), getGroupedQuery(this.termB));
  }
  static getInstance(termA: SearchQueryTerm, termB: SearchQueryTerm) {
    return new SearchQueryOr(termA, termB);
  }
}

export class SearchQueryBuilder {
  private entries: SearchQueryTerm[] = [];
  add(entry: SearchQueryTerm) {
    this.entries.push(entry);
    return this;
  }
  generateQuery() {
    const shouldGroupQuery = size(this.entries) > 1;
    return or(
      ...reject(
        map(this.entries, (entry) => {
          return shouldGroupQuery
            ? getGroupedQuery(entry)
            : isString(entry)
            ? entry
            : entry.getQuery();
        }),
        (query) => {
          return query === "";
        }
      )
    );
  }
  static getInstance() {
    return new SearchQueryBuilder();
  }
}
